import { IState } from '../reducers';
import {
    selectApprovalEnabled,
    selectArchivizationEnabled,
    selectCampaignEnabled,
    selectDelegationsEnabled,
    selectHeaderNotificationsEnabled,
    selectHeaderPaymentEnabled,
    selectHeaderPrivacyPolicyEnabled,
    selectHeaderProfileEnabled,
    selectHeaderSettingsEnabled,
    selectHeaderTermsEnabled,
    selectNewsEnabled,
    selectNormsEnabled,
    selectRegistrationFinalScreenEnabled,
    selectSignatureEnabled
} from './platformConfiguration';

const APPROVAL_MENU_ICONS = new Set(['approval', 'approvals']);

const AUTH_OR_SYSTEM_PATHS = [
    '/login',
    '/auth-callback',
    '/register',
    '/reset-password',
    '/teamsauth',
    '/redirect-teams'
];

export interface IPlatformMenuFlags {
    campaignEnabled: boolean;
    signatureEnabled: boolean;
    approvalEnabled: boolean;
    newsEnabled: boolean;
    normsEnabled: boolean;
    archivizationEnabled: boolean;
}

export interface IPlatformRouteFlags extends IPlatformMenuFlags {
    profileEnabled: boolean;
    settingsEnabled: boolean;
    paymentEnabled: boolean;
    notificationsEnabled: boolean;
    termsEnabled: boolean;
    privacyPolicyEnabled: boolean;
    registrationFinalScreenEnabled: boolean;
    delegationsEnabled: boolean;
}

export const selectPlatformMenuFlags = (state: IState): IPlatformMenuFlags => ({
    campaignEnabled: selectCampaignEnabled(state),
    signatureEnabled: selectSignatureEnabled(state),
    approvalEnabled: selectApprovalEnabled(state),
    newsEnabled: selectNewsEnabled(state),
    normsEnabled: selectNormsEnabled(state),
    archivizationEnabled: selectArchivizationEnabled(state)
});

export const selectPlatformRouteFlags = (state: IState): IPlatformRouteFlags => ({
    ...selectPlatformMenuFlags(state),
    profileEnabled: selectHeaderProfileEnabled(state),
    settingsEnabled: selectHeaderSettingsEnabled(state),
    paymentEnabled: selectHeaderPaymentEnabled(state),
    notificationsEnabled: selectHeaderNotificationsEnabled(state),
    termsEnabled: selectHeaderTermsEnabled(state),
    privacyPolicyEnabled: selectHeaderPrivacyPolicyEnabled(state),
    registrationFinalScreenEnabled: selectRegistrationFinalScreenEnabled(state),
    delegationsEnabled: selectDelegationsEnabled(state)
});

const normalizePath = (path: string): string => (path || '').toLowerCase();

const pathnameOf = (normalized: string): string =>
    normalized.split('#')[0].split('?')[0].replace(/\/+$/, '') || '/';

const isSogeDocumentationApprovalsPath = (normalized: string): boolean =>
    /\/documentation\/versions\/[^/]+\/approvals(\/|$|\?|#)/.test(normalized);

const isCampaignAcknowledgePath = (normalized: string): boolean =>
    /\/campaign\/acknowledge(\/|$|\?|#)/.test(normalized);

const isApprovalFeaturePath = (normalized: string): boolean => {
    if (isSogeDocumentationApprovalsPath(normalized)) {
        return false;
    }

    return (
        /^\/approvals(\/|$|\?|#)/.test(normalized)
        || /\/orgs\/[^/]+\/approvals(-new)?(\/|$|\?|#)/.test(normalized)
        || /\/orgs\/[^/]+\/approval-insights(\/|$|\?|#)/.test(normalized)
        || /\/clusters\/approvals?(\/|$|\?|#)/.test(normalized)
        || /\/custom-approval(\/|$|\?|#)/.test(normalized)
    );
};

export const shouldHidePlatformMenuPath = (path: string, flags: IPlatformMenuFlags): boolean => {
    const normalized = normalizePath(path);

    if (!flags.campaignEnabled && !isCampaignAcknowledgePath(normalized)
        && (/\/campaigns(\/|$|\?|#)/.test(normalized) || /\/campaign(\/|$|\?|#)/.test(normalized))) {
        return true;
    }

    if (!flags.signatureEnabled && /\/clusters\/signature(\/|$|\?|#)/.test(normalized)) {
        return true;
    }

    if (!flags.approvalEnabled && isApprovalFeaturePath(normalized)) {
        return true;
    }

    if (!flags.newsEnabled && /\/news(\/|$|\?|#)/.test(normalized)) {
        return true;
    }

    if (!flags.normsEnabled && /norms-management/.test(normalized)) {
        return true;
    }

    if (!flags.archivizationEnabled && (
        /gift-dashboard-archived/.test(normalized)
        || /\/clusters\/archive(\/|$|\?|#)/.test(normalized)
        || /\/documentation\/archives(\/|$|\?|#)/.test(normalized)
    )) {
        return true;
    }

    return false;
};

export const shouldHidePlatformMenuCta = (
    path: string,
    icon: string | undefined,
    flags: IPlatformMenuFlags
): boolean => {
    if (!flags.approvalEnabled && APPROVAL_MENU_ICONS.has((icon || '').toLowerCase())) {
        return true;
    }

    return shouldHidePlatformMenuPath(path, flags);
};

const isAuthOrSystemPath = (pathname: string): boolean => {
    if (AUTH_OR_SYSTEM_PATHS.some(prefix => pathname === prefix || pathname.startsWith(`${prefix}/`))) {
        return true;
    }

    return pathname.startsWith('/teams/');
};

export const getDisabledPlatformFeatureRedirect = (
    path: string,
    flags: IPlatformRouteFlags,
    options?: { isLoggedIn?: boolean }
): string | null => {
    const normalized = normalizePath(path);
    const pathname = pathnameOf(normalized);

    if (pathname === '/' || isAuthOrSystemPath(pathname)) {
        return null;
    }

    if (shouldHidePlatformMenuPath(path, flags)) {
        return '/';
    }

    if (flags.profileEnabled && !flags.delegationsEnabled) {
        const delegatedMatch = pathname.match(/^\/profile\/([^/]+)\/delegatedactions$/);
        if (delegatedMatch) {
            return `/profile/${delegatedMatch[1]}`;
        }
    }

    if (!flags.profileEnabled && /^\/profile(\/|$)/.test(pathname)) {
        return '/';
    }

    if (!flags.settingsEnabled && pathname === '/settings') {
        return '/';
    }

    if (!flags.paymentEnabled && pathname === '/billing') {
        return '/';
    }

    if (!flags.notificationsEnabled && pathname === '/notifications') {
        return '/';
    }

    if (options?.isLoggedIn) {
        if (!flags.termsEnabled && pathname === '/terms') {
            return '/';
        }
        if (!flags.privacyPolicyEnabled && pathname === '/copyrights') {
            return '/';
        }
    }

    if (!flags.registrationFinalScreenEnabled && pathname === '/registration-final-step') {
        return '/';
    }

    return null;
};
